import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { isForbidden } from "../../utils/isForbidden";

/**
 * SIMS-9304 AC4 — a 403 from the Actions or Action-Groups API means the user is not a
 * member of this account, so nothing on the screen can work and retrying cannot help.
 * Send them to the app's existing Unauthorized page instead of the load-failed state
 * (Design, Q-14 = C), replacing the history entry so Back does not return to a dead screen.
 *
 * Scoped to the Actions screen on purpose (D-3 = B): the shared hooks and the Roles screen
 * keep their own handling, and the 401 path stays with the session interceptor.
 *
 * Returns whether any of the given errors is a 403, so the page can render nothing while
 * the redirect happens rather than flashing load-failed with its Retry.
 */
export const useRedirectOnForbidden = (errors: readonly unknown[]): boolean => {
	const navigate = useNavigate();
	const forbidden = errors.some(isForbidden);
	const redirected = useRef(false);

	useEffect(() => {
		if (forbidden && !redirected.current) {
			redirected.current = true;
			navigate("/unauthorized", { replace: true });
		}
	}, [forbidden, navigate]);

	return forbidden;
};
